/**
 * Testing-mode stand-in for the consumer limits API
 * (`NEXT_PUBLIC_TESTING_DATA=true`, routed from `lib/api.js`).
 *
 * It keeps state for the life of the page, so a saved default or an override
 * shows up on the next GET exactly as it would against the real API. It also
 * refuses what the contract says the API refuses (a missing reason as a 422,
 * a value above 50,000 USD from a non-Super-Admin as a 403), so the error
 * paths can be exercised without a backend.
 *
 * Two behaviours here are guesses the contract does not settle, and are
 * marked where they happen: which `limits` rows the API returns for a
 * direction that is not enforced, and what makes `p2p_limited` true. The
 * screens do not depend on either; they render whatever rows and flag come
 * back.
 *
 * The import carries its `.js` extension so `node --test` can load this file
 * without a bundler (`tests/consumerLimits.test.mjs`).
 */
import {
  CONSUMER_DEFAULTS_PATH,
  DIRECTIONS,
  WINDOWS,
  SUPER_ADMIN_OVERRIDE_CEILING_USD,
  nextWeeklyReset,
  nextMonthlyReset,
} from '../utils/consumerLimits.js';

const USER_LIMITS_PATTERN = /^\/api\/v1\/admin\/users\/([^/]+)\/limits$/;

const CONTRACT_DEFAULTS = {
  currency: 'USD',
  deposit: { weekly: 3750, monthly: 10000, enabled: true },
  withdrawal: { weekly: 3750, monthly: 10000, enabled: true },
  p2p: { weekly: 3750, monthly: 10000, enabled: false },
};

/** Shaped like `ApiError` (status, response) without importing it: api.js imports this file. */
function mockApiError(status, detail) {
  const error = new Error(typeof detail === 'string' ? detail : `HTTP ${status}`);
  error.name = 'ApiError';
  error.status = status;
  error.response = { detail };
  return error;
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function hashOf(text) {
  return String(text)
    .split('')
    .reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) % 100003, 7);
}

function isPositiveNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/** 422 entries for one `{weekly, monthly}` object, FastAPI-style. */
function pairProblems(path, pair) {
  const problems = [];
  for (const { key } of WINDOWS) {
    if (!isPositiveNumber(pair?.[key])) {
      problems.push({
        loc: ['body', ...path, key],
        msg: 'Input should be greater than 0',
        type: 'greater_than',
      });
    }
  }
  if (!problems.length && pair.monthly < pair.weekly) {
    problems.push({
      loc: ['body', ...path, 'monthly'],
      msg: 'Monthly limit must be at least the weekly limit',
      type: 'value_error',
    });
  }
  return problems;
}

/**
 * Deterministic usage per user, so the same user shows the same bars on
 * every load. One user in three is at their weekly withdrawal limit, so the
 * "refused" state is easy to find.
 */
function usageFor(userId, direction, window) {
  const hash = hashOf(`${userId}:${direction}`);
  if (direction === 'p2p') return window === 'weekly' ? 40 : 310;
  if (direction === 'withdrawal' && hash % 3 === 0) {
    return window === 'weekly' ? 3750 : 6200;
  }
  const weekly = 120.5 + (hash % 30) * 100;
  return window === 'weekly' ? weekly : weekly + 1450;
}

export function createConsumerLimitsMock({
  isSuperAdmin = true,
  setBy = 'testing.admin@inversepay.app',
  now = () => new Date(),
} = {}) {
  let defaults = clone(CONTRACT_DEFAULTS);
  const overrides = new Map();

  function activeOverride(userId) {
    const override = overrides.get(String(userId));
    if (!override) return null;
    if (override.expires_at && new Date(override.expires_at) <= now()) {
      overrides.delete(String(userId));
      return null;
    }
    return override;
  }

  const service = {
    getDefaults: async () => clone(defaults),

    putDefaults: async (payload) => {
      const problems = [];
      if (payload?.currency && payload.currency !== 'USD') {
        problems.push({ loc: ['body', 'currency'], msg: "Input should be 'USD'", type: 'literal_error' });
      }
      for (const { key } of DIRECTIONS) {
        const row = payload?.[key];
        if (!row) {
          problems.push({ loc: ['body', key], msg: 'Field required', type: 'missing' });
          continue;
        }
        problems.push(...pairProblems([key], row));
        if (typeof row.enabled !== 'boolean') {
          problems.push({ loc: ['body', key, 'enabled'], msg: 'Input should be a valid boolean', type: 'bool_type' });
        }
      }
      if (problems.length) throw mockApiError(422, problems);

      defaults = {
        currency: 'USD',
        ...Object.fromEntries(
          DIRECTIONS.map(({ key }) => [
            key,
            { weekly: payload[key].weekly, monthly: payload[key].monthly, enabled: payload[key].enabled },
          ]),
        ),
      };
      return clone(defaults);
    },

    getUserLimits: async (userId) => {
      const override = activeOverride(userId);
      // Guess: P2P is limited when the default is on or this user has a P2P
      // override. The contract only says the flag exists.
      const p2pLimited = defaults.p2p.enabled || Boolean(override?.p2p);
      const resets = {
        weekly: nextWeeklyReset(now()).toISOString(),
        monthly: nextMonthlyReset(now()).toISOString(),
      };

      const limits = [];
      for (const { key } of DIRECTIONS) {
        const enforced = key === 'p2p' ? p2pLimited : defaults[key].enabled || Boolean(override?.[key]);
        // Guess: a direction that is not enforced has no rows at all.
        if (!enforced) continue;
        for (const { key: window } of WINDOWS) {
          const limit = override?.[key]?.[window] ?? defaults[key][window];
          const used = usageFor(userId, key, window);
          limits.push({
            direction: key,
            window,
            limit,
            used,
            remaining: Math.max(Math.round((limit - used) * 100) / 100, 0),
            resets_at: resets[window],
          });
        }
      }

      return { currency: 'USD', limits, p2p_limited: p2pLimited, override: override ? clone(override) : null };
    },

    putUserLimits: async (userId, payload) => {
      const problems = [];
      if (!payload?.reason || !String(payload.reason).trim()) {
        problems.push({ loc: ['body', 'reason'], msg: 'Field required', type: 'missing' });
      }
      for (const { key } of DIRECTIONS) {
        if (payload?.[key] != null) problems.push(...pairProblems([key], payload[key]));
      }
      if (problems.length) throw mockApiError(422, problems);

      const aboveCeiling = DIRECTIONS.some(({ key }) =>
        WINDOWS.some(({ key: window }) => (payload[key]?.[window] ?? 0) > SUPER_ADMIN_OVERRIDE_CEILING_USD),
      );
      if (aboveCeiling && !isSuperAdmin) {
        throw mockApiError(403, 'Limits above 50,000 USD need a Super Admin.');
      }

      const override = {
        ...Object.fromEntries(
          DIRECTIONS.map(({ key }) => [
            key,
            payload[key] ? { weekly: payload[key].weekly, monthly: payload[key].monthly } : null,
          ]),
        ),
        reason: String(payload.reason).trim(),
        expires_at: payload.expires_at || null,
        set_by: setBy,
        set_at: now().toISOString(),
      };
      overrides.set(String(userId), override);
      return service.getUserLimits(userId);
    },

    deleteUserLimits: async (userId) => {
      overrides.delete(String(userId));
      return { success: true };
    },

    /**
     * Route one `apiClient` call. Returns `null` for a path this mock does
     * not own, so `lib/api.js` falls through to its other mocks.
     */
    handle: async (endpoint, method, data) => {
      if (endpoint === CONSUMER_DEFAULTS_PATH) {
        if (method === 'GET') return service.getDefaults();
        if (method === 'PUT') return service.putDefaults(data);
        throw mockApiError(405, 'Method Not Allowed');
      }
      const match = endpoint.match(USER_LIMITS_PATTERN);
      if (match) {
        const userId = decodeURIComponent(match[1]);
        if (method === 'GET') return service.getUserLimits(userId);
        if (method === 'PUT') return service.putUserLimits(userId, data);
        if (method === 'DELETE') return service.deleteUserLimits(userId);
        throw mockApiError(405, 'Method Not Allowed');
      }
      return null;
    },
  };

  return service;
}

/** The instance testing mode uses. Acts as a Super Admin, as the mock `/auth/me` does. */
export const mockConsumerLimitsService = createConsumerLimitsMock();

export default mockConsumerLimitsService;
